import React, { useEffect, useRef, useState } from 'react';
import { useClient, PLATFORM_LABEL } from '../ClientContext';
import Icon from './Icon';
import './PlatformSwitch.css';

/**
 * Which of this client's ad accounts the page is working in.
 *
 * Modelled on the client picker in the rail: the platform, its account id
 * underneath, a tick on the current one. The id is there because "Google Ads"
 * alone does not tell a buyer WHICH Google Ads account their rules and builds
 * are about to apply to.
 *
 * One connected account still renders, as a plain label: knowing which
 * account you are in matters even when there is nothing to switch to. Only
 * a client with no account at all renders nothing.
 *
 * The badge is a BidSense lettermark, not either platform's logo.
 */

const LETTER = { meta: 'M', google: 'G' };

function Badge({ platform }) {
  return (
    <span className={`ps-badge ps-badge-${platform}`} aria-hidden="true">
      {LETTER[platform] || (platform || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function PlatformSwitch({ lockedReason = null }) {
  const { platformAccounts = [], platform, selectPlatform, selected } = useClient();
  // The client - the business the accounts belong to - named on every row,
  // so the buyer reads "BidSense Live, Google Ads, 6653910372" and not just
  // a platform and a number.
  const clientName = selected?.name || '';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Locked while switching would lose work; close any open menu with it.
  useEffect(() => { if (lockedReason) setOpen(false); }, [lockedReason]);

  if (!platformAccounts.length) return null;

  const current = platformAccounts.find((a) => a.platform === platform) || platformAccounts[0];
  const choosable = platformAccounts.length > 1;
  const locked = Boolean(lockedReason);

  return (
    <div className="ps-switch" ref={ref}>
      <button
        type="button"
        className={`ps-btn${open ? ' open' : ''}`}
        onClick={() => choosable && !locked && setOpen((o) => !o)}
        disabled={!choosable || locked}
        aria-haspopup={choosable ? 'listbox' : undefined}
        aria-expanded={choosable ? open : undefined}
        title={lockedReason || `${clientName} · ${PLATFORM_LABEL[current.platform] || current.platform} · ${current.account_id}`}
      >
        <Badge platform={current.platform} />
        <span className="ps-text">
          <span className="ps-platform">{PLATFORM_LABEL[current.platform] || current.platform}</span>
          {clientName && <span className="ps-client">{clientName}</span>}
          <span className="ps-account">{current.account_id}</span>
        </span>
        {choosable && <Icon name="down" size={13} strokeWidth={2.4} className="ps-caret" />}
      </button>

      {open && (
        <div className="rl-menu ps-menu" role="listbox" aria-label="Ad account">
          {platformAccounts.map((a) => {
            const sel = a.platform === current.platform;
            return (
              <button
                type="button"
                key={`${a.platform}:${a.account_id}`}
                role="option"
                aria-selected={sel}
                className={`rl-opt${sel ? ' sel' : ''}`}
                onClick={() => { selectPlatform(a.platform); setOpen(false); }}
              >
                <Badge platform={a.platform} />
                <span className="rl-opt-name">
                  {clientName || PLATFORM_LABEL[a.platform] || a.platform}
                  <span className="rl-opt-sub">
                    {PLATFORM_LABEL[a.platform] || a.platform} · {a.account_id}
                  </span>
                </span>
                {sel && <Icon name="check" size={14} strokeWidth={2.6} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
